import React, {ReactElement, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Trans, useTranslation} from "react-i18next";
import {useDropzone} from "react-dropzone";
import ZeeroButton from "../../custom/ZeeroButton";
import {useSession} from "../../../auth/SessionContext";
import {faPlus} from "@awesome.me/kit-92187fb46c/icons/classic/solid";
import {ParseProgress, Parser, type StudyRecap} from "../../../dicom/Parser";
import ZeeroLoading from "../../custom/ZeeroLoading";
import {Mismatch, StudyMismatch} from "../../../dicom/Mismatch";
import {buildUploadFormData} from "./UploadHandler";
import MismatchConfirmationPopup from "./MismatchConfirmationPopup";
import ProgressPopup from "../../ProgressPopup";
import RequestMetadataBox from "../../RequestMetadataBox";
import StudyRecapBox from "./StudyRecapBox";
import * as http from "../../../handlers/http";
import {ProgressPoller} from "../../../handlers/ProgressHandler";
import {CDUploadData} from "../../../types/Session";
import {CDUploadProgress, CDUploadStatus} from "../../../types/CDUpload";
import {ErrorCode, FormDataAbortedError} from "../../../types/Error";
import {toast} from "../../custom/toasters/ToastersManager";

const {FontAwesomeIcon} = await import("@fortawesome/react-fontawesome");

type ParsingStatus = "IDLE" | "PROCESSING" | "SUCCESS" | "FAILED";

export default function CDUploadPage(): ReactElement {
	const {session} = useSession();
	const {t} = useTranslation();

	const overriding = (session?.requestData as CDUploadData | undefined)?.overridingMetadata ?? null;

	const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

	const [parsingStatus, setParsingStatus] = useState<ParsingStatus>("IDLE");
	const [parsingProgress, setParsingProgress] = useState<ParseProgress>({discarded: 0, processed: 0, total: 0});
	const parsingProcessRef = useRef<AsyncGenerator<ParseProgress, Map<string, StudyRecap>> | null>(null);

	const [parsedStudies, setParsedStudies] = useState<Map<string, StudyRecap> | null>(null);
	const hasParsed = parsedStudies ? parsedStudies.size > 0 : false;

	const aborterRef = useRef<{ abort: () => void } | null>(null);
	const [uploadUuid, setUploadUuid] = useState<string | null>(null);
	const [uploadStatus, setUploadStatus] = useState<CDUploadStatus>("IDLE");
	const [uploadCounts, setUploadCounts] = useState<{ handled: number; total: number }>({total: 0, handled: 0});
	const [uploadError, setUploadError] = useState<ErrorCode | null>(null);
	const pollerRef = useRef<ProgressPoller<CDUploadProgress> | null>(null);

	const mismatches: Map<string, StudyMismatch> | null = useMemo(() => {
		if (!parsedStudies || !overriding) {
			return null;
		}
		return Mismatch.compute(overriding, parsedStudies);
	}, [parsedStudies, overriding]);

	useEffect(() => {
		return () => {
			void parsingProcessRef.current?.return(undefined as any).catch(() => {
			});
			aborterRef.current?.abort();
			pollerRef.current?.stop();
		};
	}, []);

	const startParsing = useCallback(async (files: File[]) => {
		// cancel previous parse if any
		resetAll();
		if (parsingProcessRef.current?.return) {
			await parsingProcessRef.current.return(undefined as any).catch(() => {
			});
		}

		setParsingStatus("PROCESSING");
		setParsingProgress({processed: 0, discarded: 0, total: files.length});

		const it = Parser.parse(files);
		parsingProcessRef.current = it;

		try {
			// eslint-disable-next-line no-constant-condition
			while (true) {
				const {value, done} = await it.next();
				if (done) {
					const result = value as Map<string, StudyRecap>;
					if (result.size == 0) {
						toast.error(t("cdUpload_noFileParsed"))
						setParsingStatus("FAILED");
					} else {
						setParsedStudies(result);
						setParsingStatus("SUCCESS");
					}
					return;
				}
				setParsingProgress(value as ParseProgress);
			}
		} catch (err) {
			toast.error(t("cdUpload_errorParsing"))
			console.error("error parsing files", err);
			setParsingStatus("FAILED");
		} finally {
			parsingProcessRef.current = null;
		}
	}, []);

	const {getRootProps, getInputProps, open, isDragActive} = useDropzone({
		noClick: true,
		noKeyboard: true,
		onDrop: startParsing,
		useFsAccessApi: false,
	});

	const resetUpload = useCallback(() => {
		aborterRef.current = null;
		pollerRef.current?.stop();
		pollerRef.current = null;
		setUploadUuid(null);
		setUploadStatus("IDLE");
		setUploadCounts({handled: 0, total: 0});
		setUploadError(null);
	}, [pollerRef]);

	const resetAll = useCallback(() => {
		setParsingStatus("IDLE");
		setParsingProgress({processed: 0, discarded: 0, total: 0});
		setParsedStudies(null);

		resetUpload();
	}, [resetUpload]);

	const beginPolling = useCallback(async (uuid: string) => {
		const poller = new ProgressPoller<CDUploadProgress>();
		pollerRef.current = poller;

		await poller.start(uuid, {
			onProgress: (p) => {
				setUploadStatus(p.status);
				setUploadCounts({handled: p.sent, total: p.total});
			},
			onSuccess: (p) => {
				setUploadStatus("SUCCESS");
				setUploadCounts({handled: p.sent, total: p.total});
			},
			onError: (code) => {
				setUploadStatus("FAILED");
				setUploadError(code);
			},
		});
	}, []);

	const upload = useCallback(async () => {
		if (!parsedStudies || parsedStudies.size === 0) {
			return;
		}

		setUploadStatus("SENDING");
		const form = buildUploadFormData(parsedStudies);

		try {
			const {promise, abort} = http.formData<{ uuid: string }>("/web/cd-upload", form, (loaded, total) =>
					setUploadCounts({handled: loaded, total}));
			aborterRef.current = {abort};

			const data = await promise;
			setUploadUuid(data.uuid);
			await beginPolling(data.uuid);
		} catch (e) {
			if (e instanceof FormDataAbortedError) {
				console.error("user aborted upload")
				setUploadStatus("ABORTED");
			} else {
				setUploadStatus("FAILED");
				setUploadError(ErrorCode.SERVICE_FAILED);
			}
			const total = [...form.entries()].length;
			setUploadCounts({handled: 0, total}); // nothing was sent
		} finally {
			aborterRef.current = null;
		}
	}, [parsedStudies, beginPolling]);

	const onAbort = useCallback(() => {
		setUploadStatus("ABORTING");

		aborterRef.current?.abort(); // this can happen before the upload session is initialized
		aborterRef.current = null;

		pollerRef.current?.stop();
		pollerRef.current = null;

		if (!uploadUuid) {
			return;
		}

		http.post<void>(`/web/abort?uuid=${encodeURIComponent(uploadUuid!)}`)
				.then(() => setUploadStatus("ABORTED"))
				.catch(() => {
					setUploadError(ErrorCode.SERVICE_ABORT_FAILED);
					setUploadStatus("FAILED")
				})
	}, [uploadUuid]);

	return (
			<div className="page-content" id="cd-upload">
				<div className="page-title">{t("cdUpload_cdUpload")}</div>

				<div className="upload-grid">
					{parsingStatus === "IDLE" && (
							<div
									className={`drop-zone-container ${isDragActive ? "active" : ""}`}
									{...getRootProps()}
							>
								<input {...getInputProps({dir: ""})} webkitdirectory={""}/>
								<div id="drop-zone-text">
									{t("general_dropZoneText")}
								</div>
								<ZeeroButton size="xs" type="primary" onClick={open}>
									<FontAwesomeIcon icon={faPlus}/> {t("general_dropZoneButton")}
								</ZeeroButton>
							</div>
					)}

					{parsingStatus === "PROCESSING" && parsingProgress && (
							<ZeeroLoading
									current={parsingProgress.processed}
									total={parsingProgress.total}
									message={t("cdUpload_parsing")}
							/>
					)}

					{(parsingStatus === "FAILED" || parsingStatus === "SUCCESS") && (
							<React.Fragment>
								<div id="extracted-data" className="data-container">
									<div className="recap-header">
										<div>
											<div className="upload-data-title">{t("cdUpload_foundFilesDataTitle")}</div>
											{parsingProgress && (
													<div className="upload-data-subtitle">
														<React.Fragment>
															<Trans t={t}
																   i18nKey="general_parsedTotal"
																   count={parsingProgress.total}
																   components={{strong: <strong/>}}
															/>{" "}
														</React.Fragment>
														{parsingProgress.discarded > 0 && (
																<React.Fragment>
																	<Trans t={t}
																		   i18nKey="general_parsedDiscarded"
																		   count={parsingProgress.discarded}
																		   components={{strong: <strong/>}}
																	/>{" "}
																</React.Fragment>
														)}
														{parsingProgress.discarded == parsingProgress.total
																? t("cdUpload_allFilesDiscarded")
																: t("cdUpload_extractedData")}

													</div>
											)}
										</div>
										<div className="recap-header-actions">
											<ZeeroButton type="primary" onClick={resetAll}>
												{t("general_reset")}
											</ZeeroButton>
										</div>
									</div>

									{hasParsed && Array.from(parsedStudies!.entries()).map(([studyUID, recap]) => (
											<StudyRecapBox key={studyUID} recap={recap}
														   diff={mismatches?.get(studyUID) ?? null}/>
									))}
								</div>

								{uploadStatus !== "IDLE" && (
										<ProgressPopup
												status={uploadStatus}
												progress={uploadCounts}
												error={uploadError}
												onAbort={onAbort}
												onCancel={resetUpload}
										/>
								)}
							</React.Fragment>
					)}

					<RequestMetadataBox requestMetadata={overriding} mismatches={mismatches}/>
				</div>

				{uploadStatus === "IDLE" && hasParsed && parsingStatus == "SUCCESS" && (
						<div className="upload-actions">
							<ZeeroButton
									type="primary"
									onClick={() => {
										if (mismatches && mismatches.size > 0) {
											setShowConfirmationPopup(true);
										} else {
											void upload();
										}
									}}
							>
								{t("general_upload")}
							</ZeeroButton>
						</div>
				)}

				{showConfirmationPopup && (
						<MismatchConfirmationPopup setShowConfirmationPopup={setShowConfirmationPopup} upload={upload}/>
				)}
			</div>
	);
}
